/**
 * PropertyFormDialog - Dialog para criação de nova propriedade
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Criação de propriedade para calendário de sangria
 * - Validação de limites do plano
 * - Campos: nome, localização, anos de cultivo, área, observações
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Dialog, Button, Input, Label, Textarea (shadcn/ui)
 */

import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Info icon component - ícone de informação intuitivo
const HelpIcon = ({ tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="ml-1 inline-flex items-center justify-center w-5 h-5 text-green-600 hover:text-green-800 transition-colors cursor-help">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
        <p className="text-sm leading-relaxed">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const PropertyFormDialog = ({ onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '', location: '', culture_age_years: '', total_area_hectares: '', notes: '', inicio_safra_mes: 9,
    modo_sangria: 'equipe', total_plantas_propriedade: ''
  });
  const [loading, setLoading] = useState(false);

  // Month options for harvest start
  const monthOptions = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Check plan limits first
      const limitsResponse = await axios.get(`${API}/check-plan-limits/create_property`);
      
      if (!limitsResponse.data.allowed) {
        alert(`❌ ${limitsResponse.data.message}\n\nConsidere fazer upgrade do seu plano na aba "Planos".`);
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        culture_age_years: parseInt(formData.culture_age_years),
        total_area_hectares: formData.total_area_hectares ? parseFloat(formData.total_area_hectares) : null,
        inicio_safra_mes: parseInt(formData.inicio_safra_mes),
        modo_sangria: formData.modo_sangria || 'equipe',
        total_plantas_propriedade: formData.total_plantas_propriedade ? parseInt(formData.total_plantas_propriedade) : null
      };
      await axios.post(`${API}/bleeding-properties`, submitData);
      onSave();
      onClose();
      setFormData({name: '', location: '', culture_age_years: '', total_area_hectares: '', notes: '', inicio_safra_mes: 9, modo_sangria: 'equipe', total_plantas_propriedade: ''});
      // Notificar o OnboardingChecklist para atualizar progresso
      window.dispatchEvent(new Event('heveatalent-progress-update'));
      alert("Propriedade criada com sucesso!");
    } catch (error) {
      alert("Erro ao criar propriedade: " + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('pfNewProperty')}</DialogTitle>
          <DialogDescription>
            Cadastre uma nova propriedade para o calendário de sangria
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">{t('pfPropertyName')} *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          <div>
            <Label htmlFor="location">{t('pfLocationLabel')} *</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="Ex: Acre, Brasil - Região Amazônica"
              required
            />
          </div>
          <div>
            <Label htmlFor="culture_age_years">{t('pfCultivationYears')} *</Label>
            <Input
              id="culture_age_years"
              type="number"
              value={formData.culture_age_years}
              onChange={(e) => setFormData({...formData, culture_age_years: e.target.value})}
              required
            />
          </div>
          <div>
            <Label htmlFor="total_area_hectares">{t('pfTotalAreaHa')}</Label>
            <Input
              id="total_area_hectares"
              type="number"
              step="0.1"
              value={formData.total_area_hectares}
              onChange={(e) => setFormData({...formData, total_area_hectares: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="inicio_safra_mes" className="flex items-center">
              🌳 Mês de Início da Safra *
              <HelpIcon tooltip="A safra da seringueira normalmente vai de Setembro a Agosto, mas pode variar por região. Escolha o mês em que sua safra começa para que o sistema calcule os acumulados corretamente." />
            </Label>
            <select
              id="inicio_safra_mes"
              value={formData.inicio_safra_mes}
              onChange={(e) => setFormData({...formData, inicio_safra_mes: parseInt(e.target.value)})}
              className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              A safra vai de {monthOptions.find(m => m.value === formData.inicio_safra_mes)?.label || 'Setembro'} até{' '}
              {monthOptions.find(m => m.value === (formData.inicio_safra_mes === 1 ? 12 : formData.inicio_safra_mes - 1))?.label || 'Agosto'} do ano seguinte
            </p>
          </div>

          {/* NOVO: Modo de operação da sangria */}
          <div>
            <Label htmlFor="modo_sangria" className="flex items-center">
              👥 Como a equipe trabalha?
              <HelpIcon tooltip="EQUIPE: todos os sangradores trabalham juntos no mesmo lote por dia. INDIVIDUAL: cada um tem seu próprio território e sangra lotes diferentes. FLEXÍVEL: detecta automaticamente." />
            </Label>
            <select
              id="modo_sangria"
              data-testid="property-modo-sangria"
              value={formData.modo_sangria}
              onChange={(e) => setFormData({...formData, modo_sangria: e.target.value})}
              className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="equipe">🤝 Equipe (todos sangram o mesmo lote)</option>
              <option value="individual">👤 Individual (cada um seu lote)</option>
              <option value="flexivel">🔄 Flexível (varia)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.modo_sangria === 'equipe' && 'Todos os trabalhadores recebem o mesmo nº de cortes (eles trabalham juntos).'}
              {formData.modo_sangria === 'individual' && 'Cada trabalhador conta apenas seus próprios cortes.'}
              {formData.modo_sangria === 'flexivel' && 'O sistema detecta automaticamente por dia.'}
            </p>
          </div>

          {/* NOVO: Total de plantas da propriedade */}
          <div>
            <Label htmlFor="total_plantas_propriedade" className="flex items-center">
              🌳 Total de Plantas da Propriedade
              <HelpIcon tooltip="Número total de plantas de seringueira na propriedade. Usado para calcular kg/planta/corte. Deixe em branco para usar cálculo legacy." />
            </Label>
            <Input
              id="total_plantas_propriedade"
              data-testid="property-total-plantas"
              type="number"
              min="0"
              value={formData.total_plantas_propriedade}
              onChange={(e) => setFormData({...formData, total_plantas_propriedade: e.target.value})}
              placeholder="Ex: 12000"
            />
            <p className="text-xs text-gray-500 mt-1">
              Opcional. Se preencher, será usado para calcular produtividade por planta.
            </p>
          </div>

          <div>
            <Label htmlFor="notes">{t('pfNotes')}</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Informações adicionais sobre a propriedade"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('cancel')}</Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('saving') : t('save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyFormDialog;
